import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";
import ForcePasswordChange from "./ForcePasswordChange";
import useDirectMessageToasts from "../hooks/useDirectMessageToasts";

export function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Fire in-app toasts for new direct-message activity across the whole
  // authenticated app. The hook self-throttles via its own polling loop.
  useDirectMessageToasts();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Preserve the originally requested URL so deep links from emails
    // (e.g. "View & Reply" buttons) land on the right page after sign-in
    // instead of dumping the user on /dashboard.
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" state={{ from }} replace />;
  }

  // Force-change gate: any user seeded with must_change_password=true
  // (Olivia + future invitees) must set a new password before doing
  // anything else. Renders a full-screen blocker that only lets them
  // submit the change or log out.
  if (user.must_change_password) {
    return <ForcePasswordChange />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) {
    // If the user arrived here from a protected page (e.g. via an email
    // deep link), bounce them back to that page after login.
    const from = location.state?.from;
    const target = typeof from === "string" && from.startsWith("/") ? from : "/dashboard";
    return <Navigate to={target} replace />;
  }

  return children;
}
